import { useContext, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { CYRILLIC_REGEXP } from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import i18nContext from 'components/i18n-context';
import ValidateAccountNumberModel from 'components/Templates/models/ValidateAccountNumberModel';
import { CURRENCIES } from 'services/currenciesConstant';
import { formatSortCode, isCryptoProvider } from 'services/utils';
import Input from 'uikit/Input/Input';
import Button from 'uikit/Button/Button';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import './AccountDetailsStep.scss';
import { getTemplateStepErrorMessage } from 'components/Templates/utils/getTemplateStepErrorMessage';

const AccountDetailsStep = ({ customerNumber, userAccounts, paymentStore }) => {
  const i18n = useContext(i18nContext);
  const [isCryptoCurrency, setIsCryptoCurrency] = useState(!!paymentStore.templateData?.network);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      currency: paymentStore.templateData?.currency || '',
      accountNumber:
        paymentStore.templateData?.accountNumber ||
        paymentStore.templateData?.iban ||
        paymentStore.templateData?.cryptoAddress ||
        '',
      sortCode: paymentStore.templateData?.sortCode || '',
      network: paymentStore.templateData?.network || ''
    },
    validationSchema: Yup.object().shape({
      currency: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      network: Yup.string().when('currency', {
        is: (currency) => {
          // Require network if currency is crypto
          return (
            currency &&
            (userAccounts || []).some((acc) => acc.currency === currency && isCryptoProvider(acc.transfer_provider))
          );
        },
        then: (schema) => schema.required(i18n.getMessage('client.validation.message.isEmpty')),
        otherwise: (schema) => schema.notRequired()
      }),
      accountNumber: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(255, i18n.getMessage('client.validation.message.maxSize', { size: 255 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        ),
      sortCode: Yup.string()
    }),
    onSubmit: async (values) => {
      const requestData = ValidateAccountNumberModel({ ...paymentStore.templateData, ...values });
      const templateData = { ...values, isCryptoPayment: isCryptoCurrency };

      if (isCryptoCurrency) {
        templateData.walletAddress = values.accountNumber;
      }

      paymentStore.setTemplateData(templateData);

      await paymentStore.validateTemplateAccountDetails(customerNumber, requestData, isCryptoCurrency);
    }
  });

  const { values, errors, handleSubmit, handleChange, setFieldValue } = form;

  // Currency options (unique currencies only)
  const currencyOptions = Array.from(new Set((userAccounts || []).map((account) => account.currency))).map(
    (currency) => ({
      key: currency,
      value: currency,
      isCryptoCurrency: (userAccounts || []).some(
        (acc) => acc.currency === currency && isCryptoProvider(acc.transfer_provider)
      )
    })
  );

  // Network options for selected currency
  const networkOptions = values.currency
    ? (userAccounts || [])
        .filter((acc) => acc.currency === values.currency && isCryptoProvider(acc.transfer_provider))
        .map((acc) => ({
          key: acc.network,
          value: acc.network,
          wallet: acc
        }))
    : [];

  const onCurrencyChange = (account) => {
    setFieldValue('currency', account.key);

    if (account.isCryptoCurrency) {
      setIsCryptoCurrency(true);
      // Clear network when currency changes
      setFieldValue('network', '');
    } else {
      setFieldValue('network', '');
      setIsCryptoCurrency(false);
    }
  };

  const onNetworkChange = (name, networkValue) => {
    const selectedWallet = networkOptions.find((option) => option.key === networkValue);
    setFieldValue('network', networkValue);

    // Store wallet info for later use if needed
    if (selectedWallet?.wallet) {
      // Could store additional wallet metadata here if needed
    }
  };

  const onSortCodeChange = (e) => {
    const inputValue = e.target.value;
    let numericValue = formatSortCode(inputValue);

    setFieldValue('sortCode', numericValue);
  };

  return (
    <form onSubmit={form.handleSubmit} className={'account-details-step'}>
      <div
        // eslint-disable-next-line max-len
        className={`account-details-step-inputs-wrapper ${values.currency === CURRENCIES.GBP.code ? 'gbp-max-width-wrapper' : ''}`}
      >
        <InputDropDown
          className={'account-details-step-input'}
          name={'currency'}
          label={i18n.getMessage('templates.form.currency.label')}
          placeholder={i18n.getMessage('templates.form.currency.placeholder')}
          value={values.currency}
          error={errors.currency ? errors.currency : getTemplateStepErrorMessage(i18n, paymentStore.error, 'currency')}
          onChange={(name, value) => setFieldValue('currency', value)}
          options={currencyOptions}
          isTemplateCurrency={true}
          templateCurrencyHandleChange={onCurrencyChange}
          isRequired
        />
        {values.currency && (
          <>
            {!isCryptoCurrency && (
              <Input
                className={'account-details-step-input'}
                name={'accountNumber'}
                label={i18n.getMessage('templates.form.accountNumber.label')}
                placeholder={i18n.getMessage('templates.form.accountNumber.placeholder')}
                value={values.accountNumber}
                error={
                  errors.accountNumber ||
                  getTemplateStepErrorMessage(i18n, paymentStore.error, 'accountNumber' || 'iban')
                }
                onChange={handleChange}
                isRequired
              />
            )}
            {values.currency === CURRENCIES.GBP.code && (
              <Input
                className={'account-details-step-input'}
                name={'sortCode'}
                label={i18n.getMessage('templates.form.sortCode.label')}
                placeholder={i18n.getMessage('templates.form.sortCode.placeholder')}
                value={values.sortCode}
                error={errors.sortCode || getTemplateStepErrorMessage(i18n, paymentStore.error, 'sortCode')}
                onChange={onSortCodeChange}
                max={8}
              />
            )}

            {isCryptoCurrency && (
              <InputDropDown
                className={'account-details-step-input'}
                name={'network'}
                label={i18n.getMessage('templates.form.network.label')}
                placeholder={i18n.getMessage('templates.form.network.placeholder') || 'Select network'}
                value={values.network}
                error={errors.network || getTemplateStepErrorMessage(i18n, paymentStore.error, 'network')}
                onChange={onNetworkChange}
                options={networkOptions}
                isRequired
              />
            )}
            {isCryptoCurrency && (
              <Input
                className={'account-details-step-input crypto-wallet-address-input'}
                name={'accountNumber'}
                label={i18n.getMessage('templates.form.walletAddress.label')}
                placeholder={i18n.getMessage('templates.form.walletAddress.placeholder')}
                value={values.accountNumber}
                error={errors.accountNumber || getTemplateStepErrorMessage(i18n, paymentStore.error, 'accountNumber')}
                onChange={handleChange}
                isRequired
              />
            )}
          </>
        )}
      </div>
      <Button
        className={'account-details-step-next-button'}
        type={'primary'}
        roleType={'submit'}
        onClick={handleSubmit}
        isDisabled={paymentStore.isLoading}
      >
        {i18n.getMessage('templates.form.button.next')}
      </Button>
    </form>
  );
};

AccountDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  paymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  paymentStore: stores.paymentStore
}))(observer(AccountDetailsStep));
